import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { ComponentProps, FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Field, FormSection, IconButton, InlineBanner, Input, PageHeader, RadioGroup } from '../../components/ui';
import { useApiError } from '../../hooks/useApiError';
import { useLanguage } from '../../hooks/useLanguage';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useSignOut } from '../../hooks/useSignOut';
import { getApiError, updatePassword } from '../../services/api';
import type { RootState } from '../../store';
import { fullName } from '../Users/fullName';
import { PlanSection } from './PlanSection';

const MIN_PASSWORD = 8;

type Field3 = 'current' | 'next' | 'confirm';
type Errors = Partial<Record<Field3, string>>;

const PasswordInput = ({ shown, toggle, label, ...rest }: { shown: boolean; toggle: () => void; label: [string, string] } & ComponentProps<typeof Input>) => (
    <Input
        {...rest}
        type={shown ? 'text' : 'password'}
        trailing={
            <IconButton aria-label={shown ? label[1] : label[0]} aria-pressed={shown} size="sm" onClick={toggle}>
                {shown ? <EyeSlashIcon className="size-5" aria-hidden="true" /> : <EyeIcon className="size-5" aria-hidden="true" />}
            </IconButton>
        }
    />
);

const UserProfile = () => {
    const { t } = useTranslation('team');
    usePageTitle(t('profile.pageTitle'));
    const apiError = useApiError();
    const signOut = useSignOut();
    const user = useSelector((s: RootState) => s.auth.user);
    const { code, languages, change } = useLanguage();
    const canSeePlan = user?.role?.name === 'Admin' || user?.role?.name === 'Owner';

    const [values, setValues] = useState({ current: '', next: '', confirm: '' });
    const [errors, setErrors] = useState<Errors>({});
    const [banner, setBanner] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [shown, setShown] = useState(false);
    const form = useRef<HTMLFormElement>(null);

    const validate = (v: typeof values, only?: Field3): Errors => {
        const e: Errors = {};
        if (!v.current) e.current = t('profile.required');
        if (v.next.length < MIN_PASSWORD) e.next = t('profile.tooShort');
        if (v.confirm !== v.next) e.confirm = t('profile.mismatch');
        return only ? { [only]: e[only] } : e;
    };

    const set = (key: Field3, value: string) => {
        setValues((v) => ({ ...v, [key]: value }));
        if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const blur = (key: Field3) => {
        if (values[key] === '') return;
        setErrors((e) => ({ ...e, ...validate(values, key) }));
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        const found = validate(values);
        setErrors(found);
        const first = (['current', 'next', 'confirm'] as const).find((k) => found[k]);
        if (first) {
            setBanner(null);
            form.current?.querySelector<HTMLElement>(`[data-field="${first}"]`)?.focus();
            return;
        }
        setBanner(null);
        setBusy(true);
        try {
            await updatePassword({ currentPassword: values.current, newPassword: values.next });
            // The API ends every session of the user on a password change, this one included.
            await signOut('passwordChanged');
        } catch (err) {
            if (getApiError(err).status === 400) {
                setErrors({ current: t('profile.wrongCurrent') });
                form.current?.querySelector<HTMLElement>('[data-field="current"]')?.focus();
            } else {
                setBanner(apiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    const labels: [string, string] = [t('form.show'), t('form.hide')];

    return (
        <>
            <PageHeader title={t('profile.title')} />
            <div className="grid max-w-form gap-4">
                <FormSection title={t('profile.account')} description={t('profile.accountHint')}>
                    <dl className="grid gap-3 text-small sm:grid-cols-[8rem_1fr]">
                        <dt className="text-muted">{t('profile.name')}</dt>
                        <dd className="text-strong">{user ? fullName(user) : ''}</dd>
                        <dt className="text-muted">{t('profile.email')}</dt>
                        <dd className="break-all text-strong">{user?.email}</dd>
                        <dt className="text-muted">{t('profile.role')}</dt>
                        <dd className="text-strong">{user ? t(`roles.${user.role.name}`) : ''}</dd>
                    </dl>
                </FormSection>

                <form ref={form} onSubmit={(e) => void submit(e)} noValidate>
                    <FormSection title={t('profile.password')}>
                        {/* Lets a password manager tie the new password to this account. */}
                        <input type="text" name="username" autoComplete="username" value={user?.email ?? ''} readOnly tabIndex={-1} aria-hidden="true" className="sr-only" />
                        {banner && <InlineBanner tone="danger">{banner}</InlineBanner>}
                        <Field label={t('profile.current')} error={errors.current}>
                            {(c) => (
                                <PasswordInput
                                    {...c}
                                    data-field="current"
                                    shown={shown}
                                    toggle={() => setShown((v) => !v)}
                                    label={labels}
                                    autoComplete="current-password"
                                    value={values.current}
                                    onChange={(e) => set('current', e.target.value)}
                                    required
                                />
                            )}
                        </Field>
                        <Field label={t('profile.new')} hint={t('profile.hint')} error={errors.next}>
                            {(c) => (
                                <PasswordInput
                                    {...c}
                                    data-field="next"
                                    shown={shown}
                                    toggle={() => setShown((v) => !v)}
                                    label={labels}
                                    autoComplete="new-password"
                                    value={values.next}
                                    onChange={(e) => set('next', e.target.value)}
                                    onBlur={() => blur('next')}
                                    minLength={MIN_PASSWORD}
                                    required
                                />
                            )}
                        </Field>
                        <Field label={t('profile.confirm')} error={errors.confirm}>
                            {(c) => (
                                <PasswordInput
                                    {...c}
                                    data-field="confirm"
                                    shown={shown}
                                    toggle={() => setShown((v) => !v)}
                                    label={labels}
                                    autoComplete="new-password"
                                    value={values.confirm}
                                    onChange={(e) => set('confirm', e.target.value)}
                                    onBlur={() => blur('confirm')}
                                    required
                                />
                            )}
                        </Field>
                        <div>
                            <Button type="submit" variant="strong" loading={busy}>
                                {t('profile.submit')}
                            </Button>
                        </div>
                    </FormSection>
                </form>

                <FormSection title={t('profile.language')}>
                    <RadioGroup aria-label={t('profile.languageLabel')} value={code} onChange={change} options={languages.map((l) => ({ value: l.code, label: l.label }))} />
                </FormSection>

                {canSeePlan && <PlanSection />}
            </div>
        </>
    );
};

export default UserProfile;
